/**
 * Platform Admin → Regulatory prompts. Shows the government-source changes the weekly sweep judged
 * relevant to the engine, so the owner is prompted to update a threshold or rule. It never changes a
 * calculation — each prompt is a reminder; a human makes the actual change via code, tests and the
 * actuarial review pack.
 *
 * The top line is the HEARTBEAT: when the job last ran and whether it worked, so an empty list reads as
 * "nothing relevant this week" and never as "the job quietly broke". "Run now" triggers a sweep on
 * demand (the weekly timer is the normal path). "Show AI-dismissed" reveals what the filter set aside,
 * for auditing false negatives.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, ExternalLink, CheckCircle2, Ban, Eye, RotateCcw, AlertTriangle, Rss } from 'lucide-react';
import {
  getRegulatoryPrompts,
  setRegulatoryStatus,
  runRegulatorySweep,
  explainAdminError,
  type RegulatoryPrompt,
  type RegulatoryRun,
  type RegulatoryStatus,
} from '../../services/adminApi.ts';
import { AdminMessage, SectionLabel, StatusDot } from './AdminShell.tsx';

const MODULE_LABELS: Record<string, string> = {
  aged_care: 'Residential aged care',
  support_at_home: 'Support at Home',
  age_pension: 'Age Pension & social security',
  super_tax: 'Super, tax & investments',
};

const STATUS_COLOR: Record<RegulatoryStatus, string> = {
  new: '#1a6fd4',
  reviewed: '#d97706',
  actioned: '#059669',
  dismissed: '#94a3b8',
};

const STATUS_LABEL: Record<RegulatoryStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  actioned: 'Actioned',
  dismissed: 'Dismissed',
};

const confidenceColor = (c: RegulatoryPrompt['confidence']): string =>
  c === 'high' ? '#059669' : c === 'medium' ? '#d97706' : '#94a3b8';

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const fmtDateTime = (iso: string | null): string => (iso ? new Date(iso).toLocaleString('en-AU') : '—');

type Filter = 'all' | RegulatoryStatus;
const FILTERS: Filter[] = ['all', 'new', 'reviewed', 'actioned', 'dismissed'];
const FILTER_LABEL: Record<Filter, string> = { all: 'All', new: 'New', reviewed: 'Reviewed', actioned: 'Actioned', dismissed: 'Dismissed' };

const quietButton =
  'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:opacity-50';

/** The heartbeat line: a plain-English summary of the last sweep. */
const Heartbeat = ({ run }: { run: RegulatoryRun | null }) => {
  if (!run) {
    return <p className="text-sm text-slate-500">The sweep hasn’t run yet. Use “Run now” to check the feeds for the first time.</p>;
  }
  const failed = run.ok === false || (run.finishedAt && run.ok !== true);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <StatusDot
        color={run.ok === true ? '#059669' : failed ? '#dc2626' : '#d97706'}
        label={run.ok === true ? 'Last check OK' : failed ? 'Last check had problems' : 'Running…'}
      />
      <span className="text-slate-500">
        {fmtDateTime(run.finishedAt ?? run.startedAt)} · {run.feedsChecked} feed{run.feedsChecked === 1 ? '' : 's'} · {run.itemsNew} new item
        {run.itemsNew === 1 ? '' : 's'} · {run.itemsRelevant} relevant
      </span>
      {run.error && <span className="text-red-600">{run.error}</span>}
    </div>
  );
};

const RegulatoryPanel = () => {
  const [prompts, setPrompts] = useState<RegulatoryPrompt[] | null>(null);
  const [lastRun, setLastRun] = useState<RegulatoryRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);

  const load = async (withDismissed = includeDismissed) => {
    setError(null);
    try {
      const { prompts: p, lastRun: r } = await getRegulatoryPrompts(withDismissed);
      setPrompts(p);
      setLastRun(r);
    } catch (err) {
      setPrompts([]);
      setError(explainAdminError(err, 'Could not load regulatory prompts.'));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = async (prompt: RegulatoryPrompt, status: RegulatoryStatus) => {
    setBusyId(prompt.id);
    setNotice(null);
    try {
      await setRegulatoryStatus(prompt.id, status);
      setPrompts((prev) => (prev ? prev.map((p) => (p.id === prompt.id ? { ...p, status } : p)) : prev));
    } catch (err) {
      setNotice({ tone: 'error', text: explainAdminError(err, 'Could not update the prompt.') });
    } finally {
      setBusyId(null);
    }
  };

  const sweepNow = async () => {
    setSweeping(true);
    setNotice(null);
    try {
      const { result } = await runRegulatorySweep();
      setNotice({
        tone: 'success',
        text: result.skipped
          ? 'A sweep is already running — try again shortly.'
          : `Checked ${result.feedsChecked} feed${result.feedsChecked === 1 ? '' : 's'}: ${result.itemsNew} new item${result.itemsNew === 1 ? '' : 's'}, ${result.itemsRelevant} relevant.`,
      });
      await load();
    } catch (err) {
      setNotice({ tone: 'error', text: explainAdminError(err, 'The sweep could not be completed.') });
    } finally {
      setSweeping(false);
    }
  };

  const toggleDismissed = async () => {
    const next = !includeDismissed;
    setIncludeDismissed(next);
    setPrompts(null);
    await load(next);
  };

  const visible = (prompts ?? []).filter((p) => filter === 'all' || p.status === filter);

  return (
    <div className="space-y-6">
      {notice && <AdminMessage tone={notice.tone}>{notice.text}</AdminMessage>}
      {error && <AdminMessage tone="error">{error}</AdminMessage>}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3">
        <Heartbeat run={lastRun} />
        <div className="flex items-center gap-2">
          <button type="button" onClick={toggleDismissed} className={quietButton} title="Show items the AI judged not relevant">
            <Eye size={14} /> {includeDismissed ? 'Hide AI-dismissed' : 'Show AI-dismissed'}
          </button>
          <button type="button" onClick={sweepNow} disabled={sweeping} className={quietButton}>
            {sweeping ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Run now
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <SectionLabel>Prompts</SectionLabel>
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                filter === f ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {FILTER_LABEL[f]}
            </button>
          ))}
        </div>
      </div>

      {prompts === null ? (
        <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
          <Loader2 size={18} className="animate-spin" /> Loading prompts…
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-slate-500">
          <Rss size={26} className="mx-auto mb-3 text-slate-300" />
          {filter === 'all'
            ? 'No regulatory prompts yet. The weekly sweep will add any it finds — or use “Run now”.'
            : `No ${FILTER_LABEL[filter].toLowerCase()} prompts.`}
        </div>
      ) : (
        <ul className="space-y-4">
          {visible.map((p) => (
            <li
              key={p.id}
              className={`rounded-xl border border-slate-200 p-5 ${p.status === 'dismissed' || !p.relevant ? 'opacity-60' : ''}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {p.relevant ? (
                      <StatusDot color={STATUS_COLOR[p.status]} label={STATUS_LABEL[p.status]} />
                    ) : (
                      <StatusDot color="#94a3b8" label="AI-dismissed" />
                    )}
                    {p.confidence && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                        style={{ background: confidenceColor(p.confidence) }}
                      >
                        {p.confidence} confidence
                      </span>
                    )}
                    <span className="text-xs text-slate-500">
                      {p.sourceLabel} · published {fmtDate(p.publishedAt)} · found {fmtDate(p.detectedAt)}
                    </span>
                  </div>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 text-base font-semibold text-slate-900 hover:text-[#1a6fd4]"
                  >
                    {p.title} <ExternalLink size={14} className="shrink-0 text-slate-400" />
                  </a>
                </div>
              </div>

              {p.summary && <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-700">{p.summary}</p>}

              {p.affectedModules.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.affectedModules.map((m) => (
                    <span key={m} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                      {MODULE_LABELS[m] ?? m}
                    </span>
                  ))}
                </div>
              )}

              {p.sourceUrls.length > 0 && (
                <div className="mt-3 text-xs text-slate-500">
                  <span className="font-semibold">Read:</span>{' '}
                  {p.sourceUrls.map((u, i) => (
                    <React.Fragment key={u}>
                      {i > 0 && ' · '}
                      <a href={u} target="_blank" rel="noreferrer" className="underline hover:text-[#1a6fd4]">
                        source {i + 1}
                      </a>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {p.relevant && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  {p.status !== 'reviewed' && (
                    <button type="button" disabled={busyId === p.id} onClick={() => changeStatus(p, 'reviewed')} className={quietButton}>
                      {busyId === p.id ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Mark reviewed
                    </button>
                  )}
                  {p.status !== 'actioned' && (
                    <button
                      type="button"
                      disabled={busyId === p.id}
                      onClick={() => changeStatus(p, 'actioned')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:border-emerald-300 disabled:opacity-50"
                    >
                      {busyId === p.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Mark actioned
                    </button>
                  )}
                  {p.status !== 'dismissed' && (
                    <button
                      type="button"
                      disabled={busyId === p.id}
                      onClick={() => changeStatus(p, 'dismissed')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                    >
                      {busyId === p.id ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Dismiss
                    </button>
                  )}
                  {p.status !== 'new' && (
                    <button type="button" disabled={busyId === p.id} onClick={() => changeStatus(p, 'new')} className={quietButton}>
                      {busyId === p.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Reopen
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-xs leading-relaxed text-slate-500">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-slate-400" />
        <span>
          These prompts are reminders drawn from public government sources — they never change a calculation. Confirm the detail at the
          source, then make any change through the usual code, tests and actuarial review.
        </span>
      </div>
    </div>
  );
};

export default RegulatoryPanel;
